import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import {
  LayoutDashboard, Building2, FileText, LineChart, ListChecks, UserRound,
  Settings, Archive, Users, Upload, LogOut, Search, Menu, X, Bell, HelpCircle,
  BarChart3,
} from "lucide-react";
import OnboardingGuide, { hasSeenOnboarding } from "./OnboardingGuide";

function Logo({ className="" }) {
  return (
    <div className={`flex items-center justify-center w-full px-2 py-1 ${className}`}>
      <img src="/cesco-logo-landscape-white.png" alt="Cesco Equipment"
           className="max-w-full h-auto object-contain" style={{ maxHeight: 56 }} />
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState(null);
  const searchRef = useRef(null);
  const [notifs, setNotifs] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Auto-open onboarding for first-time users (once per user, stored in localStorage)
  useEffect(() => {
    if (user?.id && !hasSeenOnboarding(user.id)) {
      setShowOnboarding(true);
    }
  }, [user?.id]);

  async function loadNotifs() {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch (e) { /* polled every 60s - silent to avoid console spam */ }
  }
  useEffect(() => {
    loadNotifs();
    // Pause polling when the tab is hidden - saves backend queries on idle tabs.
    const id = setInterval(() => {
      if (document.visibilityState === "visible") loadNotifs();
    }, 60000);
    const onVis = () => { if (document.visibilityState === "visible") loadNotifs(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setResults(null); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(searchQ)}`);
        setResults(data);
      } catch (e) { /* debounced per keystroke - silent to avoid console spam */ }
    }, 200);
    return () => clearTimeout(h);
  }, [searchQ]);

  useEffect(() => {
    function onDoc(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setResults(null);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const isAdmin = user?.role === "admin";
  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/companies",  icon: Building2, label: "Companies" },
    { to: "/contacts",   icon: UserRound, label: "Contacts" },
    { to: "/quotes",     icon: FileText, label: "Quotes" },
    { to: "/pipeline",   icon: LineChart, label: "Pipeline" },
    { to: "/tasks",      icon: ListChecks, label: "Tasks" },
    { to: "/reports",    icon: BarChart3, label: "Reports" },
  ];
  const adminItems = [
    { to: "/admin/users",        icon: Users, label: "Users" },
    { to: "/admin/archive",      icon: Archive, label: "Archive" },
    { to: "/admin/import",       icon: Upload, label: "Import/Export" },
  ];

  return (
    <div className="min-h-screen flex bg-[var(--cesco-bg)]">
      {/* Sidebar (desktop) + drawer (mobile) */}
      <aside
        className={`fixed lg:static z-40 top-0 left-0 h-full w-64 bg-[#11151D] text-white
          transform ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          transition-transform duration-200 flex flex-col`}
        data-testid="sidebar"
      >
        <div className="p-4 border-b border-white/5 flex items-start gap-2">
          <Logo />
          <button className="lg:hidden text-white/70 shrink-0" onClick={() => setMenuOpen(false)}
                  data-testid="sidebar-close"><X size={20}/></button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map(i => (
            <NavLink key={i.to} to={i.to}
              className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}
              onClick={() => setMenuOpen(false)}
              data-testid={`nav-${i.label.toLowerCase()}`}>
              <i.icon size={18} /> {i.label}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="pt-3 pb-1 px-3 text-[11px] uppercase tracking-widest text-slate-500">Admin</div>
              {adminItems.map(i => (
                <NavLink key={i.to} to={i.to}
                  className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`nav-admin-${i.label.toLowerCase()}`}>
                  <i.icon size={18} /> {i.label}
                </NavLink>
              ))}
            </>
          )}
          <NavLink to="/settings"
            className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}
            onClick={() => setMenuOpen(false)}
            data-testid="nav-settings">
            <Settings size={18} /> Settings
          </NavLink>
          <button
            className="nav-link w-full"
            onClick={() => { setShowOnboarding(true); setMenuOpen(false); }}
            data-testid="nav-help">
            <HelpCircle size={18} /> Help / Tour
          </button>
        </nav>
        <div className="p-3 border-t border-white/5 text-sm">
          <div className="px-2 pb-2">
            <div className="text-white font-semibold">{user?.name}</div>
            <div className="text-slate-400 text-xs">{user?.email}</div>
            <div className="mt-1">
              <span className={`pill ${isAdmin ? "pill-green" : "pill-blue"}`}>{user?.role}</span>
            </div>
          </div>
          <button className="nav-link w-full" onClick={async()=>{ await logout(); nav("/login"); }}
            data-testid="btn-logout">
            <LogOut size={18}/> Log out
          </button>
        </div>
      </aside>
      {menuOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={()=>setMenuOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-[var(--cesco-border)]">
          <div className="flex items-center gap-3 px-3 sm:px-5 py-3">
            <button className="lg:hidden text-gray-700" onClick={()=>setMenuOpen(true)}
                    data-testid="btn-open-menu"><Menu size={22}/></button>
            <div ref={searchRef} className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                className="cesco-input pr-9"
                placeholder="Search companies, contacts, quote numbers…"
                value={searchQ}
                onChange={e=>setSearchQ(e.target.value)}
                data-testid="global-search-input"
              />
              {results && (searchQ.length > 0) && (
                <div className="absolute mt-1 w-full bg-white border rounded-lg shadow-lg max-h-96 overflow-auto z-40"
                     data-testid="global-search-results">
                  {results.companies?.length > 0 && (
                    <div>
                      <div className="px-3 py-1 text-[11px] uppercase text-gray-500">Companies</div>
                      {results.companies.map(c => (
                        <Link key={c.id} to={`/companies/${c.id}`} onClick={()=>{setResults(null); setSearchQ("");}}
                          className="block px-3 py-2 hover:bg-[var(--theme-accent-50)]"
                          data-testid="search-result-company">
                          <div className="font-medium">{c.company_name}</div>
                          <div className="text-xs text-gray-500">{c.state} · Tier {c.customer_tier}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {results.contacts?.length > 0 && (
                    <div>
                      <div className="px-3 py-1 text-[11px] uppercase text-gray-500">Contacts</div>
                      {results.contacts.map(c => (
                        <Link key={c.id} to={`/companies/${c.company_id}`} onClick={()=>{setResults(null); setSearchQ("");}}
                          className="block px-3 py-2 hover:bg-[var(--theme-accent-50)]"
                          data-testid="search-result-contact">
                          <div className="font-medium">{c.first_name} {c.last_name}</div>
                          <div className="text-xs text-gray-500">at {c.company_name}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {results.quotes?.length > 0 && (
                    <div>
                      <div className="px-3 py-1 text-[11px] uppercase text-gray-500">Quotes</div>
                      {results.quotes.map(q => (
                        <Link key={q.id} to={`/quotes/${q.id}`} onClick={()=>{setResults(null); setSearchQ("");}}
                          className="block px-3 py-2 hover:bg-[var(--theme-accent-50)]"
                          data-testid="search-result-quote">
                          <div className="font-medium">{q.quote_number} · {q.company_name}</div>
                          <div className="text-xs text-gray-500">{q.status}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {!results.companies?.length && !results.contacts?.length && !results.quotes?.length && (
                    <div className="p-3 text-sm text-gray-500">No results for "{searchQ}"</div>
                  )}
                </div>
              )}
            </div>
            <div ref={bellRef} className="relative ml-auto">
              <button
                className="relative w-10 h-10 rounded-lg border border-[var(--cesco-border)] flex items-center justify-center hover:bg-[var(--theme-accent-50)]"
                onClick={() => setBellOpen(o => !o)}
                data-testid="notif-bell"
                aria-label="Notifications"
              >
                <Bell size={18} />
                {notifs?.count > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center"
                        data-testid="notif-count">
                    {notifs.count > 99 ? "99+" : notifs.count}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 mt-2 w-80 max-w-[92vw] bg-white border rounded-xl shadow-xl z-40 overflow-hidden"
                     data-testid="notif-panel">
                  <div className="px-4 py-2 border-b flex items-center justify-between">
                    <div className="font-semibold">Notifications</div>
                    <button className="text-xs text-gray-500 hover:text-black" onClick={loadNotifs}
                            data-testid="notif-refresh">Refresh</button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {(!notifs || notifs.items.length === 0) && (
                      <div className="p-6 text-center text-sm text-gray-500">
                        Nothing needs your attention 🎉
                      </div>
                    )}
                    {notifs?.items.map(n => (
                      <Link key={n.id} to={n.link}
                        className="block px-4 py-3 border-b hover:bg-[var(--theme-accent-50)]"
                        onClick={() => setBellOpen(false)}
                        data-testid={`notif-item-${n.kind}`}>
                        <div className="flex items-start gap-2">
                          <span className={`pill ${n.severity === "high" ? "pill-red" : "pill-blue"}`}>
                            {({
                              task_overdue: "OVERDUE",
                              task_today: "DUE TODAY",
                              quote_reminder: "REMINDER",
                              quote_at_risk: "AT RISK",
                            })[n.kind] || n.kind.toUpperCase()}
                          </span>
                        </div>
                        <div className="mt-1 font-medium text-sm truncate">{n.title}</div>
                        <div className="text-xs text-gray-500 truncate">{n.subtitle}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto">
          {children}
        </main>
      </div>

      {showOnboarding && (
        <OnboardingGuide
          userName={user?.name || ""}
          userId={user?.id}
          onClose={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}
